import { useEffect, useState } from "react";
import { ghostBtnClass, inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { uploadBuilderAssistantPhoto } from "@/lib/manufacturing/builderAssistantClient";
import { listQcCorrectionPhotos, saveQcCorrection } from "@/lib/manufacturing/qcDashboardClient";
import type { QcCorrectionLine } from "@/lib/manufacturing/qcCorrection";

export function BuilderAssistantCorrectionList({
  sessionId,
  lines,
  disabled,
  onFlushPending,
  onChanged,
}: {
  sessionId: string;
  lines: QcCorrectionLine[];
  disabled: boolean;
  onFlushPending?: (flush: () => Promise<{ ok: true } | { ok: false; error: string }>) => void;
  onChanged: () => void;
}) {
  const [drafts, setDrafts] = useState<Record<string, string>>(() =>
    Object.fromEntries(lines.map((line) => [line.observation_id, line.action])),
  );
  const [photos, setPhotos] = useState<Record<string, { url: string; kind: "failure" | "fix" }[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDrafts(Object.fromEntries(lines.map((line) => [line.observation_id, line.action])));
  }, [lines]);

  useEffect(() => {
    let cancelled = false;
    void listQcCorrectionPhotos(sessionId).then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error);
        return;
      }
      const next: Record<string, { url: string; kind: "failure" | "fix" }[]> = {};
      for (const photo of res.data.photos) {
        next[photo.observation_id] = [...(next[photo.observation_id] ?? []), { url: photo.cdn_url, kind: photo.kind }];
      }
      setPhotos(next);
    });
    return () => {
      cancelled = true;
    };
  }, [sessionId, lines]);

  useEffect(() => {
    onFlushPending?.(() => {
      const actions = lines.map((line) => ({
        observation_id: line.observation_id,
        action: drafts[line.observation_id] ?? "",
      }));
      return saveQcCorrection(sessionId, actions).then((res) => (res.ok ? { ok: true as const } : res));
    });
  }, [onFlushPending, sessionId, lines, drafts]);

  async function addPhoto(observationId: string, file: File) {
    setBusy(true);
    setError(null);
    const saved = await saveQcCorrection(
      sessionId,
      lines.map((line) => ({ observation_id: line.observation_id, action: drafts[line.observation_id] ?? "" })),
    );
    if (!saved.ok) {
      setBusy(false);
      setError(saved.error);
      return;
    }
    const uploaded = await uploadBuilderAssistantPhoto(observationId, file, "fix");
    setBusy(false);
    if (!uploaded.ok) {
      setError(uploaded.error);
      return;
    }
    setPhotos((current) => ({
      ...current,
      [observationId]: [...(current[observationId] ?? []), { url: uploaded.cdn_url, kind: "fix" }],
    }));
    onChanged();
  }

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Correction checklist</h3>
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          Only the open failures from the original report. Describe the correction and add a new photo. Dictate below if something was missed.
        </p>
      </div>
      {lines.length === 0 ? <p className="text-sm text-gray-600 dark:text-zinc-400">Nothing left to correct.</p> : null}
      <ul className="space-y-3">
        {lines.map((line) => (
          <li key={line.observation_id} className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-zinc-700">
            <p className="text-sm font-medium text-gray-900 dark:text-zinc-100">
              {line.category} — {line.title}
            </p>
            {line.failure_note && line.failure_note !== line.title ? (
              <p className="text-sm text-gray-700 dark:text-zinc-300">{line.failure_note}</p>
            ) : null}
            <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
              Corrective action
              <textarea
                className={`${inputClass} mt-1 min-h-16 w-full`}
                value={drafts[line.observation_id] ?? ""}
                disabled={disabled || busy}
                placeholder="What was done to correct this"
                onChange={(event) => setDrafts((current) => ({ ...current, [line.observation_id]: event.target.value }))}
              />
            </label>
            {(photos[line.observation_id] ?? []).some((photo) => photo.kind === "failure") ? (
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-700 dark:text-zinc-300">Failure photos</p>
                <ul className="flex flex-wrap gap-2">
                  {(photos[line.observation_id] ?? []).filter((photo) => photo.kind === "failure").map((photo) => (
                    <li key={photo.url}>
                      <img src={photo.url} alt="" className="h-16 w-16 rounded object-cover" />
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {(photos[line.observation_id] ?? []).some((photo) => photo.kind === "fix") ? (
              <ul className="flex flex-wrap gap-2">
                {(photos[line.observation_id] ?? []).filter((photo) => photo.kind === "fix").map((photo) => (
                  <li key={photo.url}>
                    <img src={photo.url} alt="" className="h-16 w-16 rounded object-cover" />
                  </li>
                ))}
              </ul>
            ) : null}
            <label className={`${ghostBtnClass} inline-flex cursor-pointer`}>
              Correction photo
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                className="hidden"
                disabled={disabled || busy}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file) void addPhoto(line.observation_id, file);
                }}
              />
            </label>
          </li>
        ))}
      </ul>
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
    </section>
  );
}
